// @ts-check
// OFFERS store — Zustand (Overmind retirement, Phase D1). Owns the CLIENT-side,
// POST-MOUNT FULL-offers fetch that was the Overmind `shopItemsRequiredPageRendered`
// action + `getShopItems`/`refetchShopItemsForShippingCountry` operators + the
// `shopItems`/`shopItemsFull`/`shopItemsLoading`/`shopItemsCountry` store fields.
//
// THE TWO OFFERS SOURCES (unchanged contract)
//   1. SSR + first-render source = the SERVER-THREADED grid-projected offers
//      (lib/regionPageData → projectOffersForShopGrid), delivered through the
//      <RegionOffersProvider shopItems={...}> context. Galleries are CAPPED there
//      (task #76). This store does NOT touch that path.
//   2. Post-mount FULL (uncapped) offers = THIS store. A product/checkout/shop page
//      fires `ensureFullOffers(region)` in a mount effect, which fetches the
//      complete offers (/api/getAllHardwareOffers) so galleries show every image +
//      set/bundle items (the #187 upgrade). components/RegionOffers.useRegionOffers
//      prefers this store's FULL offers once `full` is set, exactly as it used to
//      prefer state.shopItems once state.shopItemsFull was set.
//
// SSR-SAFETY — every field starts at its EMPTY default (`full:false`, `offersFull:[]`).
// This store is a client module singleton; it is NEVER read/written during the
// server render (the fetch runs only in post-mount effects). So the SSR render and
// the client's FIRST render both see `full:false` → useRegionOffers returns the
// server-prop grid offers on BOTH sides → SSR HTML === first client render (no
// React #418/#423), and no per-request offers leak across concurrent SSR requests.
//
// WHY ZUSTAND (not provider-local state) — the full-offers fetch is triggered AND
// read from surfaces OUTSIDE the page-content <RegionOffersProvider> tree: the
// /restore-cart route (its own island) and the chrome shopping-cart side panel
// (rendered in the server <SiteChrome> shell). A React context provider could not
// reach those; a GLOBAL singleton store does — the same reason the cart/region
// slices are Zustand (store/cartStore.js, store/regionStore.js).
//
// REGION CHANGES — the footer region switch is a HARD navigation (a plain <a href>),
// so it reloads the page and resets this store; the fresh page re-fetches for the
// new region. The ONLY in-place region change is the payment-page country picker
// (Overmind selectedCountryChanged), which bridges into this store via
// operators.refetchOffersStoreForRegion. So `useRegionOffers` never needs a
// region-freshness key on the READ path — once `full`, the store's offers are for
// the page's current region.
import { create } from 'zustand'
import { degradeOffersImages } from '../lib/imageGuards'
import { useRegionStore } from './regionStore'

/** @typedef {import('../lib/schemas').ShopItems} ShopItems */

export const useOffersStore = create((set, get) => ({
  /** @type {ShopItems} the FULL (uncapped) offers loaded post-mount; [] until then */
  offersFull: [],
  /**
   * AUTHORITATIVE "we hold the FULL offers" flag (mirrors the former
   * state.shopItemsFull). NOT length-derived — it is set true only after a real
   * /api/getAllHardwareOffers response commits, so a product page can tell the
   * grid-projected SSR data (which useRegionOffers still serves until then) apart
   * from the complete galleries. SSR/first-render default is false.
   */
  full: false,
  /** true while a full-offers fetch is in flight (mirrors state.shopItemsLoading) */
  loading: false,
  /**
   * The country the current `offersFull` were fetched for — the #188 DEDUP KEY
   * (mirrors state.shopItemsCountry). Also claimed synchronously at fetch start so
   * a superseding fetch (region change / shipping-country refinement) wins
   * last-desired and a stale in-flight response is discarded on commit.
   * @type {string|null}
   */
  country: null,

  /**
   * Fetch + commit the FULL offers for `target`. Shared by ensureFullOffers /
   * refetchForShippingCountry / the region-change bridge. Mirrors
   * operators.getShopItems: degrade the images at the client trust boundary, set
   * full=true, and adopt the API-detected country when `target` is null. Claims
   * `country` synchronously so concurrent fetches resolve last-desired-wins.
   * @param {string|null} target
   * @returns {Promise<void>}
   */
  _fetchOffers: async (target) => {
    set({ loading: true, country: target })
    try {
      const res = await fetch(`/api/getAllHardwareOffers?region=${target}`)
      if (!res.ok) throw new Error(`getAllHardwareOffers ${res.status}`)
      const data = await res.json()
      // A newer fetch (region change / shipping refinement) superseded this one —
      // discard the stale response so it can't clobber the desired offers.
      if (get().country !== target) return
      set({
        offersFull: degradeOffersImages(data.shopItems),
        full: true,
        country: target == null ? data.country : target,
        loading: false,
      })
    } catch (e) {
      // Keep full=false so useRegionOffers falls back to the server-prop grid and
      // the gate retries on the next mount (byte-identical to getShopItems' catch,
      // which left shopItemsFull false). Only clear loading if we still own it.
      if (get().country === target) set({ loading: false })
    }
  },

  /**
   * Post-mount FULL-offers gate + fetch. Mirrors the
   * `if (!shopItemsFull && !shopItemsLoading) shopItemsRequiredPageRendered()`
   * trigger the four checkout forks / SpecsPage / BF2024Page / the shop fallback /
   * restore-cart shared. Idempotent: skips when the full offers for `region` are
   * already held or in flight (the #188 anti-refetch dedup).
   * @param {string|null} [region]  the pricing country (useRegionCountryCode());
   *                                defaults to the region store's resolved value.
   * @returns {Promise<void>}
   */
  ensureFullOffers: async (region) => {
    const target = region === undefined ? useRegionStore.getState().newRegion : region
    const s = get()
    // Already have (or are fetching) the full offers for this country. When target
    // is null we cannot do better than whatever we already loaded, so don't loop.
    if (s.full && (s.country === target || target == null)) return
    if (s.loading && s.country === target) return
    await get()._fetchOffers(target)
  },

  /**
   * Sub-region VAT/availability refinement. Mirrors the OFFERS half of the former
   * operators.refetchShopItemsForShippingCountry (#188): when the visitor's precise
   * shipping country differs from the region representative we priced against
   * (GB/FR/IT/ES on /eu), re-fetch the full offers for that precise country. The
   * shippingInfo half is refined alongside this in hooks/useRegionChange
   * (refineForShippingCountry), which orchestrates both.
   *
   * DEDUP KEY = the country the currently-displayed offers were priced against.
   * On the SERVER-PROPS path the store never client-fetched, so `s.country` stays
   * null and the displayed offers are the SSR grid priced against the region
   * representative — fall back to `regionStore.newRegion` for the comparison.
   * Gating on `!s.country` (the old guard) skipped the refine for EVERY sub-region
   * country because `s.country` is null on that path, so GB kept the EU catalog +
   * "Ireland" ships-from instead of the UK's Corby stock. `!representative` still
   * short-circuits a bare content page (no offers, no region) — the #188 intent.
   * @param {string|null} cc  the precise shipping country (region store)
   * @returns {Promise<void>}
   */
  refetchForShippingCountry: async (cc) => {
    const s = get()
    const representative = s.country || useRegionStore.getState().newRegion
    if (!cc || !representative || cc === representative) return
    await get()._fetchOffers(cc)
  },

  /**
   * Directly set the FULL offers for a country (bridge for an already-fetched
   * response — not used by the self-fetch path). Claims the country so a racing
   * in-flight fetch is discarded on commit.
   * @param {any[]} offers  ALREADY image-degraded offers
   * @param {string|null} country
   */
  setOffers: (offers, country) =>
    set({ offersFull: Array.isArray(offers) ? offers : [], full: true, country: country ?? null, loading: false }),
}))

// ---- selectors ----
/** @param {{ offersFull: any[] }} s */
export const selectOffersFull = (s) => s.offersFull
/** @param {{ full: boolean }} s */
export const selectOffersFullFlag = (s) => s.full
/** @param {{ loading: boolean }} s */
export const selectOffersLoading = (s) => s.loading
